import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mountain, Snowflake, Wifi, Car, Coffee, Utensils, MapPin, Phone, Mail, Calendar, Users, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const AlpineRetreat = () => {
  const navigate = useNavigate();
  const [bookingData, setBookingData] = useState({
    checkIn: '',
    checkOut: '',
    guests: '2',
    rooms: '1'
  });

  const handleBooking = (e) => {
    e.preventDefault();
    alert('This is a demo site. Booking functionality is not active.');
  };

  const amenities = [
    { icon: Mountain, name: "Ski-in/Ski-out Access" },
    { icon: Snowflake, name: "Heated Pool & Spa" },
    { icon: Wifi, name: "High-Speed WiFi" },
    { icon: Car, name: "Free Parking" },
    { icon: Coffee, name: "Breakfast Included" },
    { icon: Utensils, name: "Fine Dining Restaurant" }
  ];

  const rooms = [
    { name: "Alpine Suite", size: "45m²", price: "CHF 450", image: "https://images.unsplash.com/photo-1591088398332-8a7791972843?w=600&q=80", features: ["Mountain View", "King Bed", "Fireplace", "Balcony"] },
    { name: "Deluxe Room", size: "35m²", price: "CHF 320", image: "https://images.unsplash.com/photo-1590490360182-c33d57733427?w=600&q=80", features: ["Valley View", "Queen Bed", "Mini Bar"] },
    { name: "Family Chalet", size: "75m²", price: "CHF 680", image: "https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?w=600&q=80", features: ["Panoramic View", "2 Bedrooms", "Kitchen", "Private Sauna"] }
  ];

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Back Button */}
      <button
        onClick={() => navigate('/')}
        className="fixed top-6 left-6 z-50 bg-amber-700 text-white p-3 rounded-full shadow-lg hover:bg-amber-800 transition-all duration-300 hover:scale-110"
      >
        <ArrowLeft className="h-6 w-6" />
      </button>

      {/* Hero Section */}
      <header className="relative h-screen">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=1920&q=80"
            alt="Alpine Resort"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-stone-50"></div>
        </div>

        <div className="relative h-full flex flex-col">
          {/* Nav */}
          <nav className="container mx-auto px-6 py-8 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <Mountain className="h-10 w-10 text-white" />
              <span className="text-2xl font-light text-white tracking-wide">Alpine Retreat</span>
            </div>
            <div className="hidden md:flex items-center gap-8 text-white/90 text-sm">
              <button onClick={() => document.getElementById('rooms').scrollIntoView({ behavior: 'smooth' })} className="hover:text-amber-300 transition-colors">Rooms</button>
              <button onClick={() => document.getElementById('amenities').scrollIntoView({ behavior: 'smooth' })} className="hover:text-amber-300 transition-colors">Amenities</button>
              <button onClick={() => document.getElementById('booking').scrollIntoView({ behavior: 'smooth' })} className="hover:text-amber-300 transition-colors">Book Now</button>
            </div>
          </nav>

          {/* Hero Content */}
          <div className="flex-1 flex items-center justify-center text-center px-6">
            <div className="max-w-4xl">
              <p className="text-amber-300 tracking-[0.3em] uppercase text-sm mb-6">Swiss Alps • 1,800m Altitude</p>
              <h1 className="text-6xl md:text-8xl font-light text-white mb-6 leading-tight">
                Escape to the<br />
                <span className="italic">Mountains</span>
              </h1>
              <p className="text-xl text-white/80 max-w-2xl mx-auto mb-10">
                A luxury alpine sanctuary where pristine nature meets Swiss hospitality excellence
              </p>
              <Button 
                onClick={() => document.getElementById('booking').scrollIntoView({ behavior: 'smooth' })}
                className="bg-amber-700 hover:bg-amber-800 text-white px-10 py-7 text-lg rounded-none"
              >
                Check Availability
              </Button>
            </div>
          </div>

          {/* Scroll Indicator */}
          <div className="absolute bottom-12 left-1/2 -translate-x-1/2 text-white/60">
            <ChevronDown className="h-8 w-8 animate-bounce" />
          </div>
        </div>
      </header>

      {/* Rooms Section */}
      <section id="rooms" className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-amber-700 tracking-widest uppercase text-sm mb-4">Accommodations</p>
            <h2 className="text-5xl font-light text-stone-800">Our Rooms & Suites</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {rooms.map((room, i) => (
              <div key={i} className="group">
                <div className="relative h-72 overflow-hidden mb-6">
                  <img
                    src={room.image}
                    alt={room.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <h3 className="text-2xl font-light text-stone-800 mb-2">{room.name}</h3>
                <p className="text-stone-500 mb-4">{room.size}</p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {room.features.map((feature, j) => (
                    <span key={j} className="text-xs bg-stone-100 text-stone-600 px-3 py-1">{feature}</span>
                  ))}
                </div>
                <p className="text-amber-700 text-xl font-light">From {room.price}<span className="text-stone-500 text-sm"> / night</span></p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Amenities Section */}
      <section id="amenities" className="py-24 bg-stone-100">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-amber-700 tracking-widest uppercase text-sm mb-4">Experience</p>
            <h2 className="text-5xl font-light text-stone-800">Resort Amenities</h2>
          </div>

          <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-8">
            {amenities.map((amenity, i) => (
              <div key={i} className="text-center group">
                <div className="w-20 h-20 mx-auto mb-4 bg-white rounded-full flex items-center justify-center group-hover:bg-amber-700 transition-colors duration-300">
                  <amenity.icon className="h-8 w-8 text-amber-700 group-hover:text-white transition-colors duration-300" />
                </div>
                <p className="text-stone-700 text-sm">{amenity.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Booking Section */}
      <section id="booking" className="py-24 bg-stone-800 text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <p className="text-amber-400 tracking-widest uppercase text-sm mb-4">Reservations</p>
              <h2 className="text-5xl font-light">Book Your Stay</h2>
            </div>

            <form onSubmit={handleBooking} className="bg-stone-700/50 p-10">
              <div className="grid md:grid-cols-4 gap-6 mb-8">
                <div>
                  <Label className="text-stone-300 mb-2 block text-sm">Check-in</Label>
                  <Input
                    type="date"
                    value={bookingData.checkIn}
                    onChange={(e) => setBookingData({...bookingData, checkIn: e.target.value})}
                    className="bg-stone-600 border-stone-500 text-white h-12"
                    required
                  />
                </div>
                <div>
                  <Label className="text-stone-300 mb-2 block text-sm">Check-out</Label>
                  <Input
                    type="date"
                    value={bookingData.checkOut}
                    onChange={(e) => setBookingData({...bookingData, checkOut: e.target.value})}
                    className="bg-stone-600 border-stone-500 text-white h-12"
                    required
                  />
                </div>
                <div>
                  <Label className="text-stone-300 mb-2 block text-sm">Guests</Label>
                  <select
                    value={bookingData.guests}
                    onChange={(e) => setBookingData({...bookingData, guests: e.target.value})}
                    className="w-full h-12 px-3 bg-stone-600 border border-stone-500 text-white rounded-md"
                  >
                    {[1,2,3,4,5,6].map(n => <option key={n} value={n}>{n} Guest{n > 1 ? 's' : ''}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-stone-300 mb-2 block text-sm">Rooms</Label>
                  <select
                    value={bookingData.rooms}
                    onChange={(e) => setBookingData({...bookingData, rooms: e.target.value})}
                    className="w-full h-12 px-3 bg-stone-600 border border-stone-500 text-white rounded-md"
                  >
                    {[1,2,3,4].map(n => <option key={n} value={n}>{n} Room{n > 1 ? 's' : ''}</option>)}
                  </select>
                </div>
              </div>
              <Button type="submit" className="w-full bg-amber-700 hover:bg-amber-600 text-white py-6 text-lg">
                Check Availability
              </Button>
              <p className="text-center text-stone-400 text-xs mt-4 italic">* Demo site - booking not functional</p>
            </form>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div className="flex flex-col items-center">
              <MapPin className="h-8 w-8 text-amber-700 mb-4" />
              <p className="text-stone-800 font-medium">Location</p>
              <p className="text-stone-500">Zermatt, Switzerland</p>
            </div>
            <div className="flex flex-col items-center">
              <Phone className="h-8 w-8 text-amber-700 mb-4" />
              <p className="text-stone-800 font-medium">Reservations</p>
              <p className="text-stone-500">+41 27 966 0000</p>
            </div>
            <div className="flex flex-col items-center">
              <Mail className="h-8 w-8 text-amber-700 mb-4" />
              <p className="text-stone-800 font-medium">Email</p>
              <p className="text-stone-500">stay@alpineretreat.ch</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-stone-900 text-center">
        <p className="text-stone-500 text-sm">© 2026 Alpine Retreat • Demo Site</p>
      </footer>
    </div>
  );
};

export default AlpineRetreat;
